/**
 * Study selector dropdown for switching between studies.
 */

import { useState } from 'react'
import {
  Check,
  ChevronDown,
  FolderOpen,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { useStudyStore, Study } from '@/stores/studyStore'
import { useAuthContext } from '@/hooks/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { navigateToStudy, navigateToMainApp } from '@/lib/subdomain'

// Fetch studies from API
async function fetchStudies(): Promise<Study[]> {
  const response = await api.get('/studies')
  return response.data.data || []
}

export function StudySelector() {
  const { isAuthenticated } = useAuthContext()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const { currentStudy, setCurrentStudy } = useStudyStore()

  const { data: studies = [], isLoading } = useQuery({
    queryKey: ['studies'],
    queryFn: fetchStudies,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isAuthenticated,
  })

  const filteredStudies = studies.filter(
    (study) =>
      study.name.toLowerCase().includes(search.toLowerCase()) ||
      study.studyId.toLowerCase().includes(search.toLowerCase())
  )

  const handleSelectStudy = (study: Study) => {
    setCurrentStudy(study)
    setOpen(false)
    // Navigate to study subdomain (default path will redirect to first configured page)
    navigateToStudy(study.studyId)
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      Active: 'default',
      'In Setup': 'secondary',
      Completed: 'outline',
      Archived: 'secondary',
    }
    return (
      <Badge variant={variants[status] || 'secondary'} className="ml-auto">
        {status}
      </Badge>
    )
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full md:w-[280px] justify-between"
        >
          {currentStudy ? (
            <span className="flex items-center gap-2 truncate">
              <FolderOpen className="h-4 w-4 shrink-0" />
              <span className="truncate">{currentStudy.name}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground">
              <FolderOpen className="h-4 w-4 shrink-0" />
              Select a study...
            </span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[280px] p-0" align="start">
        {/* Search */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search studies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        <ScrollArea className="max-h-[300px]">
          {/* All studies */}
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {search ? 'Search Results' : 'All Studies'}
          </DropdownMenuLabel>

          {isLoading ? (
            <div className="p-2 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : filteredStudies.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {search ? 'No studies found' : 'No studies available'}
            </div>
          ) : (
            filteredStudies.map((study) => (
              <DropdownMenuItem
                key={study.studyId}
                onClick={() => handleSelectStudy(study)}
                className="cursor-pointer"
              >
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="truncate font-medium">{study.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {study.studyId} - {study.phase}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(study.status)}
                  {currentStudy?.studyId === study.studyId && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>

        {/* Actions */}
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => {
              setOpen(false)
              navigateToMainApp('/studies')
            }}
          >
            <FolderOpen className="mr-2 h-4 w-4" />
            View all studies
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
